/**
 * IOP (Single Sign) OAuth 2.0 utilities for PKCE authorization code flow.
 */

const IOP_STATE_KEY = 'iop_oauth_state'
const IOP_VERIFIER_KEY = 'iop_code_verifier'

function generateRandomString(length: number): string {
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  return Array.from(array, (byte) => byte.toString(36).padStart(2, '0'))
    .join('')
    .slice(0, length)
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder()
  return crypto.subtle.digest('SHA-256', encoder.encode(plain))
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  bytes.forEach((b) => (binary += String.fromCharCode(b)))
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/[=]+$/, '')
}

export async function generatePKCE(): Promise<{verifier: string; challenge: string}> {
  const verifier = generateRandomString(64)
  const hashed = await sha256(verifier)
  const challenge = base64UrlEncode(hashed)
  return {verifier, challenge}
}

export function getIopBaseUrl(): string {
  return import.meta.env.VITE_IOP_BASE_URL || ''
}

export function getIopClientId(): string {
  return import.meta.env.VITE_IOP_CLIENT_ID || ''
}

export function getIopRedirectUri(): string {
  return `${window.location.origin}/oauth/callback`
}

export function getIopPlatformGuid(): string {
  return import.meta.env.VITE_IOP_PLATFORM_GUID || ''
}

export function isIopEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_IOP_LOGIN === 'true'
}

/**
 * Optional signup prefill data forwarded to IOP alongside login_hint (KRAF-711).
 * IOP pre-fills (but does not lock) these registration fields; the email from
 * login_hint is the only locked field. Sent as snake_case params per IOP's
 * /oauth/authorize contract.
 */
export interface IopSignupPrefill {
  firstName?: string
  lastName?: string
  phoneNumber?: string
}

/**
 * OIDC prompt sent on every IOP authorize (KRAF-743). `select_account` makes IOP
 * show its account chooser whenever the browser still holds a live Single Sign
 * session, so a user our idle timer signed out picks their account and is back
 * in with one click. The previous `login` forced a fresh password on every
 * visit — it predates IOP's account chooser (KRAF-715), which is now the guard
 * against silently continuing as whoever was last signed in on the browser.
 */
export const IOP_AUTHORIZE_PROMPT = 'select_account'

/**
 * Build the IOP authorize URL and store PKCE + state in sessionStorage.
 */
export async function buildIopAuthorizeUrl(
  loginHint?: string,
  prefill?: IopSignupPrefill,
): Promise<string> {
  const {verifier, challenge} = await generatePKCE()
  const state = generateRandomString(32)

  sessionStorage.setItem(IOP_STATE_KEY, state)
  sessionStorage.setItem(IOP_VERIFIER_KEY, verifier)

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: getIopClientId(),
    redirect_uri: getIopRedirectUri(),
    scope: 'openid profile email offline_access',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    platform_guid: getIopPlatformGuid(),
    prompt: IOP_AUTHORIZE_PROMPT,
  })

  if (loginHint) {
    params.set('login_hint', loginHint)
  }

  const prefillEntries: Array<[string, string | undefined]> = [
    ['first_name', prefill?.firstName],
    ['last_name', prefill?.lastName],
    ['phone_number', prefill?.phoneNumber],
  ]
  for (const [key, raw] of prefillEntries) {
    const value = raw?.trim()
    if (value) {
      params.set(key, value)
    }
  }

  return `${getIopBaseUrl()}/oauth/authorize?${params.toString()}`
}

/**
 * Retrieve and clear the stored PKCE verifier + state from sessionStorage.
 */
export function consumeOAuthSession(): {state: string; verifier: string} | null {
  const state = sessionStorage.getItem(IOP_STATE_KEY)
  const verifier = sessionStorage.getItem(IOP_VERIFIER_KEY)

  if (!state || !verifier) return null

  sessionStorage.removeItem(IOP_STATE_KEY)
  sessionStorage.removeItem(IOP_VERIFIER_KEY)

  return {state, verifier}
}
